// src/components/EventPurchaseDrawer.jsx
import React, { useMemo, useState } from 'react';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from '@/components/ui/drawer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Minus, Plus } from 'lucide-react';
import { currencyFmt } from '@/utils/ui';
import api from '../../lib/api';
import { useTranslation } from 'react-i18next';

const OTHER_RATE = 0.063845; // 6.3845% combined "taxes & others"

export default function EventPurchaseDrawer({
  open,
  onClose,
  eventObj,
  groupCurrency = 'USD',
}) {
  const { t } = useTranslation();

  const [qty, setQty] = useState(1);
  const ccy = eventObj?.currency || groupCurrency || 'USD';
  const unitPrice = Number(eventObj?.price || 0);

  const { subtotal, combinedFee, total } = useMemo(() => {
    const sub = unitPrice * qty;
    const fee = sub * OTHER_RATE;
    return { subtotal: sub, combinedFee: fee, total: sub + fee };
  }, [unitPrice, qty]);

  const onBuy = async () => {
    try {
      if (unitPrice <= 0) {
        // free => just register
        await api.post(`/events/${eventObj.id}/register`);
        onClose?.({ ok: true, freeRegistered: true });
        return;
      }
      const { data } = await api.post(`/checkout/event`, {
        eventId: eventObj.id,
        quantity: qty,
      });
      window.location.href = data.url; // Stripe Checkout
    } catch (e) {
      onClose?.({
        ok: false,
        error:
          e?.response?.data?.detail ||
          t(
            'groupSpace.eventPurchase.startCheckoutError'
          ),
      });
    }
  };

  if (!eventObj) return null;

  return (
    <Drawer
      open={open}
      onOpenChange={(v) => !v && onClose?.()}
    >
      <DrawerContent className="sm:max-w-lg ml-auto rounded-l-2xl">
        <DrawerHeader>
          <DrawerTitle>
            {t('groupSpace.eventPurchase.title', {
              title: eventObj.title,
            })}
          </DrawerTitle>
          <p className="text-sm text-slate-600">
            {eventObj.location ||
              t('groupSpace.eventPurchase.locationTBA')}
          </p>
        </DrawerHeader>

        <div className="px-6 space-y-4">
          <div className="flex items-center justify-between">
            <span>{t('groupSpace.eventPurchase.price')}</span>
            <span className="font-semibold">
              {currencyFmt(unitPrice, ccy)}
            </span>
          </div>

          <div className="flex items-center justify-between">
            <span>
              {t('groupSpace.eventPurchase.quantity')}
            </span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setQty((q) => Math.max(1, q - 1))
                }
              >
                <Minus size={14} />
              </Button>
              <Badge>{qty}</Badge>
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setQty((q) => q + 1)
                }
              >
                <Plus size={14} />
              </Button>
            </div>
          </div>

          <div className="h-px bg-slate-200 my-2" />

          <div className="flex items-center justify-between text-sm">
            <span>
              {t('groupSpace.eventPurchase.subtotal')}
            </span>
            <span>{currencyFmt(subtotal, ccy)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span>
              {t('groupSpace.eventPurchase.taxes')}
            </span>
            <span>{currencyFmt(combinedFee, ccy)}</span>
          </div>

          <div className="flex items-center justify-between text-base font-semibold">
            <span>
              {t('groupSpace.eventPurchase.total')}
            </span>
            <span>{currencyFmt(total, ccy)}</span>
          </div>
        </div>

        <DrawerFooter className="px-6">
          <Button onClick={onBuy}>
            {unitPrice > 0
              ? t('groupSpace.eventPurchase.proceedPaid')
              : t('groupSpace.eventPurchase.registerFree')}
          </Button>
          <Button
            variant="ghost"
            onClick={() => onClose?.()}
          >
            {t('groupSpace.eventPurchase.close')}
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
